import { ClickAwayListener } from "@mui/base";
import type { PopperPlacementType } from "@mui/base/Popper";
import {
  type JSXElementConstructor,
  type KeyboardEvent,
  memo,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dropdown } from "../../dropdown";
import { Flex } from "../../flex";
import {
  type RedoButtonSize,
  type RedoButtonTheme,
} from "../buttons/redo-button";
import { BaseRedoInput } from "../input/base-redo-text-input";
import { RedoCommandMenuItemComponent } from "./redo-command-menu-item";
import { RedoCommandMenuSubmenuItem } from "./redo-command-menu-submenu-item";
import * as redoCommandMenuCss from "./redo-command-menu.module.css";

export interface RedoCommandMenuItem {
  Icon?: JSXElementConstructor<any>;
  text: string;
  onClick?(event: MouseEvent<HTMLButtonElement>): void;
  dataTestId?: string;
  dataTrackId?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  tooltip?: string;
  theme?: RedoButtonTheme;
  size?: RedoButtonSize;
  show?: boolean;
  id?: string;
  shortcutHotkey?: (string | number)[];
  TrailingIcon?: JSXElementConstructor<any>;
  children?: RedoCommandMenuItem[];
  customSubmenuContent?:
    | ReactNode
    | ((anchor: HTMLElement | null) => ReactNode);
  submenuPlacement?: PopperPlacementType;
  /**
   * Keep the submenu open when the pointer leaves the trigger (instead of the
   * default hover-close). Use for `customSubmenuContent` that the user interacts
   * with — e.g. a multi-select list — so it stays open until they dismiss it.
   */
  keepSubmenuOpenOnLeave?: boolean;
}

export interface RedoCommandMenuSeparator {
  readonly _type: "separator";
}

export const REDO_COMMAND_MENU_SEPARATOR: RedoCommandMenuSeparator = {
  _type: "separator",
} as const;

export type RedoCommandMenuEntry =
  | RedoCommandMenuItem
  | RedoCommandMenuSeparator;

export function isSeparator(
  entry: RedoCommandMenuEntry,
): entry is RedoCommandMenuSeparator {
  return "_type" in entry && entry._type === "separator";
}

function isSubmenuItem(item: RedoCommandMenuItem): boolean {
  return item.children !== undefined || item.customSubmenuContent !== undefined;
}

export const RedoCommandMenu = memo(function RedoCommandMenu({
  dontRestrainIconWidth,
  items,
  open,
  setOpen,
  anchor,
  size = "sm",
  fitToAnchor = false,
  placement,
  dropdownClassName,
  searchable = false,
  searchPlaceholder = "Search...",
}: {
  dontRestrainIconWidth?: boolean;
  items: RedoCommandMenuEntry[];
  open: boolean;
  setOpen(value: boolean): void;
  anchor: HTMLElement | null;
  size?: RedoButtonSize;
  fitToAnchor?: boolean;
  placement?: PopperPlacementType;
  dropdownClassName?: string;
  /** Show a search box that filters the top-level items by their text. */
  searchable?: boolean;
  /** Placeholder for the search box (only used when `searchable`). */
  searchPlaceholder?: string;
}) {
  const atLeastOneIcon = items.some(
    (entry) => !isSeparator(entry) && entry.Icon !== undefined,
  );
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const [prevOpen, setPrevOpen] = useState(open);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Reset transient state (open submenu, search query) when the menu closes.
  // Done during render rather than in an effect — see
  // https://react.dev/learn/you-might-not-need-an-effect.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) {
      setOpenSubmenuIndex(null);
      setSearchText("");
    }
  }

  const shownEntries = useMemo(
    () => items.filter((entry) => isSeparator(entry) || entry.show !== false),
    [items],
  );

  // When searching, narrow to items whose text matches and drop separators so
  // none are left dangling. With no query, show everything as-is.
  const entriesToShow = useMemo(() => {
    if (!searchable || searchText.trim() === "") {
      return shownEntries;
    }
    const query = searchText.trim().toLowerCase();
    return shownEntries.filter(
      (entry) =>
        !isSeparator(entry) && entry.text.toLowerCase().includes(query),
    );
  }, [shownEntries, searchable, searchText]);

  // Clean up stale refs when items change
  buttonRefs.current.length = entriesToShow.length;

  // Focus first non-disabled item when menu opens. Skip this when searchable so
  // the search box keeps focus and the user can type immediately.
  useEffect(() => {
    if (open && !searchable) {
      // Delay needed because dropdown renders via portal
      requestAnimationFrame(() => {
        const firstEnabledIndex = entriesToShow.findIndex(
          (entry) => !isSeparator(entry) && !entry.disabled,
        );
        if (firstEnabledIndex >= 0) {
          setFocusedIndex(firstEnabledIndex);
          buttonRefs.current[firstEnabledIndex]?.focus({ preventScroll: true });
        }
      });
    }
  }, [open, entriesToShow, searchable]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // When a submenu is open, let the submenu handle ArrowUp/ArrowDown
      if (openSubmenuIndex !== null) {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          return;
        }
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          e.stopPropagation();
          setOpenSubmenuIndex(null);
          buttonRefs.current[openSubmenuIndex]?.focus();
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          setOpenSubmenuIndex(null);
          buttonRefs.current[openSubmenuIndex]?.focus();
          return;
        }
      }

      const enabledIndices = entriesToShow
        .map((entry, idx) =>
          !isSeparator(entry) && !entry.disabled ? idx : -1,
        )
        .filter((idx) => idx !== -1);

      if (enabledIndices.length === 0) {
        return;
      }

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const currentPos = enabledIndices.indexOf(focusedIndex);
          const nextPos =
            currentPos === -1 ? 0 : (currentPos + 1) % enabledIndices.length;
          const nextIndex = enabledIndices[nextPos];
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          setFocusedIndex(nextIndex);
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          buttonRefs.current[nextIndex]?.focus();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const currentPos = enabledIndices.indexOf(focusedIndex);
          const prevPos =
            currentPos === -1
              ? 0
              : (currentPos - 1 + enabledIndices.length) %
                enabledIndices.length;
          const prevIndex = enabledIndices[prevPos];
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          setFocusedIndex(prevIndex);
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          buttonRefs.current[prevIndex]?.focus();
          break;
        }
        case "ArrowRight": {
          const focusedEntry = entriesToShow[focusedIndex];
          if (
            focusedEntry &&
            !isSeparator(focusedEntry) &&
            isSubmenuItem(focusedEntry)
          ) {
            e.preventDefault();
            setOpenSubmenuIndex(focusedIndex);
          }
          break;
        }
        case "Escape": {
          e.preventDefault();
          setOpen(false);
          anchor?.focus();
          break;
        }
      }
    },
    [
      entriesToShow,
      focusedIndex,
      setOpen,
      anchor,
      openSubmenuIndex,
      setOpenSubmenuIndex,
    ],
  );

  const dropdown = (
    <Dropdown
      anchor={anchor}
      className={dropdownClassName}
      fitToAnchor={fitToAnchor}
      // Drop the blanket content padding so the search header's bottom border
      // can span edge to edge; each section pads itself instead (mirrors
      // RedoListDropdown's header/body split).
      flexProps={{ p: "none" }}
      open={open}
      placement={placement}
    >
      <div onKeyDown={handleKeyDown} role="menu">
        {searchable && (
          <Flex
            borderBottomWidth="1px"
            borderColor="primary"
            borderStyle="solid"
            px="md"
            py="sm"
          >
            <BaseRedoInput
              autoFocus
              onKeyDown={(e) => {
                // Hand off to the list on ArrowDown so the user can navigate
                // without leaving the keyboard. Stop propagation so the menu's
                // own key handler doesn't also step the focus.
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  e.stopPropagation();
                  const firstEnabled = entriesToShow.findIndex(
                    (entry) => !isSeparator(entry) && !entry.disabled,
                  );
                  if (firstEnabled >= 0) {
                    setFocusedIndex(firstEnabled);
                    buttonRefs.current[firstEnabled]?.focus();
                  }
                }
              }}
              placeholder={searchPlaceholder}
              setValue={setSearchText}
              size="sm"
              value={searchText}
            />
          </Flex>
        )}
        <Flex dir="column" gap="xs" p="md">
          {entriesToShow.map((entry, idx) => {
            if (isSeparator(entry)) {
              return (
                <div
                  className={redoCommandMenuCss.separator}
                  key={`separator-${idx}`}
                  role="separator"
                />
              );
            }

            if (isSubmenuItem(entry)) {
              return (
                <RedoCommandMenuSubmenuItem
                  buttonRef={(el) => {
                    buttonRefs.current[idx] = el;
                  }}
                  closeMenu={() => setOpen(false)}
                  dontRestrainIconWidth={dontRestrainIconWidth}
                  fitToAnchor={fitToAnchor}
                  indentForIcon={atLeastOneIcon}
                  isOpen={openSubmenuIndex === idx}
                  item={entry}
                  key={entry.id ?? idx}
                  onClose={() => {
                    setOpenSubmenuIndex(null);
                    buttonRefs.current[idx]?.focus();
                  }}
                  onOpen={() => setOpenSubmenuIndex(idx)}
                  size={size}
                />
              );
            }

            return (
              <RedoCommandMenuItemComponent
                dontRestrainIconWidth={dontRestrainIconWidth}
                fitToAnchor={fitToAnchor}
                indentForIcon={atLeastOneIcon}
                item={entry}
                itemClicked={() => setOpen(false)}
                key={entry.id ?? idx}
                ref={(el) => {
                  buttonRefs.current[idx] = el;
                }}
                size={size}
              />
            );
          })}
        </Flex>
      </div>
    </Dropdown>
  );

  return (
    <>
      {open && (
        <ClickAwayListener
          onClickAway={() => {
            // If a submenu is open and the user clicks, it means they
            // are clicking on the submenu (because it closers when the
            // user isn't hovering over it), so we don't want to close the menu.
            if (openSubmenuIndex !== null) {
              return;
            }
            setOpen(false);
          }}
        >
          {dropdown}
        </ClickAwayListener>
      )}
    </>
  );
});
